import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { FinnJob, FinnStatus, Job } from '../../core/job/job.model';
import { Company, FinnAdSetting } from '../../core/company/company.model';

import { environment } from '../../../environments/environment';
import {
  FinnIndustry,
  FinnJobTypes,
  FinnManagerTypes,
  FinnOccupation,
  FinnPartnerId,
  FinnStatics
} from '../models/finn.model';
import { User } from '../../shared/models/user/user.model';
import { ErrorHandlingService } from '../../shared/services/error-handling/error-handling.service';
import { TranslateService } from '@ngx-translate/core';
import { DatesService } from '../../shared/services/dates/dates.service';
import { PromotionService } from '../../promotion/services/promotion.service';
import { DateTime } from 'luxon';
import moment from 'moment';


export interface FinnMessage {
    message: string;
    level: string;
    name: string;
    value: string;
}

export interface FinnResponse {
    data: FinnJob;
    messages: FinnMessage[];
}

@Injectable({
  providedIn: 'root',
})
export class FinnService {
    private apiUrl = environment.apiUrl;
    private entityUrl = this.apiUrl + 'finn/';
    DEFAULT_FINN_DURATION = 41;
    DATE_FORMAT = 'yyyy-MM-dd';

    private datesService = inject(DatesService);

    constructor(
        private readonly http: HttpClient,
        private readonly errorService: ErrorHandlingService,
        private readonly translate: TranslateService,
        private readonly promotionService: PromotionService
    ) { }

    public fetchIndustries(): Observable<FinnIndustry[]> {
        return this.http.get<FinnIndustry[]>(this.entityUrl + 'industries/');
    }

    public fetchStatics(): Observable<FinnStatics> {
        return this.http.get<FinnStatics>(this.entityUrl + 'jobs/statics/');
    }

    public fetchOccupations(): Observable<FinnOccupation[]> {
        return this.http.get<FinnOccupation[]>(this.entityUrl + 'occupations/');
    }

    saveFinnJob(finnJob: FinnJob, params: {
      publish: 0 | 1,
      preview?: 0 | 1,
      jobId?: number
    }): Observable<FinnJob> {
      const { publish, preview, jobId } = params;

      const queryParams: any = {};

      if (publish) queryParams.publish = '1';
      if (!publish) queryParams.draft = '1';
      if (preview) queryParams.preview = '1';

      return this.isExisting(finnJob)
        ? this.http.put<FinnJob>(`${this.entityUrl}jobs/${finnJob.id}/`, finnJob, { params: queryParams })
        : this.http.post<FinnJob>(`${this.entityUrl}jobs/`, {
          job_id: jobId,
          ...finnJob
        }, { params: queryParams });
    }

    publishFinnJob(finnJob: FinnJob, jobId?: number): Observable<FinnJob> {
      return this.isExisting(finnJob)
        ? this.http.put<FinnJob>(`${this.entityUrl}jobs/${finnJob.id}/`, finnJob)
        : this.http.post<FinnJob>(`${this.entityUrl}jobs/?publish=1`, {
          job_id: jobId,
          ...finnJob
        });
    }

    public previewFinnJob(finnJob: FinnJob, jobId?: number): Observable<any> {
      return this.isExisting(finnJob)
        ? this.http.put<FinnJob>(`${this.entityUrl}jobs/${finnJob.id}/?preview=1`, finnJob)
        : this.http.post<FinnJob>(`${this.entityUrl}jobs/?preview=1`, {
          job_id: jobId,
          ...finnJob
        });
    }

    public fetchFinnJob(jobId: number): Observable<FinnJob> {
        return this.http.get<FinnJob>(this.entityUrl + `jobs/by-cvideo-job/${jobId}/`);
    }

    public deactivateFinnJob(finnJobId: number): Observable<FinnJob> {
        return this.http.delete<FinnJob>(this.entityUrl + `jobs/${finnJobId}/`);
    }

    public getFinnJobs(companyId: number): Observable<FinnJob[]> {
        let params = new HttpParams();
        params = params.append('company_id', `${companyId}`);

        return this.http.get<FinnJob[]>( `${this.entityUrl}jobs/`, {
          params
        });
    }

    deleteFinnJobs(jobsIds: number[]): Observable<any> {
      return this.http.delete(`${this.entityUrl}jobs/`, {
        body: {
          finn_job_ids: jobsIds
        }
      });
    }

    public createPartnerId(companyId: number, partnerId: FinnPartnerId): Observable<any> {
      return this.http.post<FinnJob>(`${this.entityUrl}${companyId}/partner-id/`, partnerId);
    }

    public deletePartnerId(companyId: number, partnerId: number): Observable<any> {
      return this.http.delete(`${this.entityUrl}${companyId}/partner-id/${partnerId}/`);
    }

    public updatePartnerId(companyId: number, partnerId: number, partnerIdObj: FinnPartnerId): Observable<any> {
      return this.http.put(`${this.entityUrl}${companyId}/partner-id/${partnerId}/`, partnerIdObj);
    }

    getFinnAdvancedSettings(ads: FinnAdSetting[]): any {
      if (!ads) {
        return {};
      }

      const restructuredObject = {};

      ads.forEach(ad => {
        const { partner_id, logo, company_name, about_us } = ad;
        restructuredObject[partner_id] = { logo, company_name, about_us };
      });

      return restructuredObject;
    }

    logUnexpectedError(details: {
      user: User,
      company: Company,
      job: Job,
      error: HttpErrorResponse,
      finnJob: FinnJob
    }): void {
      const info = {
        user: `${details.user?.first_name} ${details.user?.last_name} (id: ${details.user?.id})`,
        company: `${details.company?.name} (id: ${details.company?.id})`,
        job: `${details.job?.title} (id: ${details.job?.id})`,
        status: details.error?.status,
        error: details.error?.error || details.error,
        finnJob: details.finnJob
      };

      const title = 'Unexpected error response when publishing Finn job. User experiences general \'Smth went wrong\' message.';
      const text = JSON.stringify(info, null, 2);

      this.errorService.logMessageToSlack({ title, text });
    }

  /**
   * Creates a default Finn job from a NUU job
   *
   * Initializes a new Finn job in DRAFT status with data from the provided NUU job.
   * Sets the start date to today and calculates the end date (capped at 41 days or job deadline).
   *
   * @param data - Object containing job and invoice reference
   * @param data.job - The NUU job to create Finn job from
   * @param data.invoice_reference - Invoice reference (will be truncated to 30 characters)
   * @returns A new FinnJob object with default values and data copied from the job
   *
   * @remarks
   * - Uses translated description from job.translations if available, falls back to job.description
   * - Includes gallery_images from job if present
   * - Sets country to 'Norway' by default
   * - Initializes empty arrays for industries, tags, occupations, and job_contacts
   * - Status is set to FinnStatus.DRAFT
   **/
    createDefaultFinn(data: {
      job: Job,
      invoice_reference: string
    }): FinnJob {
      const { job, invoice_reference } = data;

      const today = this.datesService.now();
      const start = today.toFormat(this.DATE_FORMAT);
      const jobDeadline = job?.expiration_date;
      const end = this.getFinnEndDate(today, jobDeadline);

      const jobDefaultLanguage = job.languages.find(l => l.is_default)?.code;

      return {
        id: null,
        finn_partner_id: null,
        partner_id_name: null,
        partner_id: null,
        industries: [],
        tags: [],
        images: job.gallery_images || [],
        occupations: [],
        job_contacts: [],
        job_type: FinnJobTypes.JOB,
        manager_role: FinnManagerTypes.MIDMANAGER,
        application_email: '',
        company_url: '',
        facebook_url: '',
        linkedin_url: '',
        twitter_handle: '',
        video_url: '',
        ingress: '',
        order_no: '',
        title: job?.title || '',
        header: job?.header || '',
        description: job?.translations[jobDefaultLanguage]?.description || job.description,
        street_address: '',
        zip_code: '',
        country: 'Norway',
        country_code: 'no',
        application_url: '',
        number_of_positions: 1,
        sector: '',
        duration: '',
        stopped: false,
        published: false,
        finn_url: '',
        invoice_reference: invoice_reference.slice(0, 30),
        advert_start_date: start,
        advert_stop_date: end,
        expired_at: '',
        expiration_date: '',
        first_publish_by: null,
        statistics_link: '',
        is_expired: false,
        status: FinnStatus.DRAFT
      };
    }

  /**
   * Finn duration is 42 days by default
   * BUT if job deadline is earlier - then Finn end date is capped at job deadline
   *
   * @param start - The start date for the Finn ad
   * @param jobDeadline - Optional job deadline that may cap the Finn duration
   * @returns The calculated end date for the Finn ad
   **/
    getFinnEndDate(start: DateTime, jobDeadline?: DateTime): DateTime {
      const finnMaxPossible = start.plus({ days: this.DEFAULT_FINN_DURATION }).toFormat(this.DATE_FORMAT);

      if (!jobDeadline) {
        return finnMaxPossible;
      }

      return jobDeadline < finnMaxPossible ? jobDeadline : finnMaxPossible;
    }

  /**
   * Finn duration is 41 days by default
   * BUT if job deadline is earlier - then Finn end date is capped at job deadline
   *
   * This version uses Moment.js for date handling and delegates to PromotionService
   * for consistent date calculation logic across all promotion types.
   *
   * @param start - The start date for the Finn ad (Moment)
   * @param jobDeadline - Optional job deadline that may cap the Finn duration (Moment)
   * @returns The calculated end date for the Finn ad (Moment)
   **/
    getFinnEndDateMoment(start: moment.Moment, jobDeadline?: moment.Moment): moment.Moment {
      const finnMaxPossible = this.getFinnMaxPossible(start);
      return this.promotionService.calculatePromotionEndDate(finnMaxPossible, jobDeadline);
    }

  /**
   * Calculates the maximum possible Finn ad end date based on the default duration (41 days)
   * Delegates to PromotionService for consistent date calculation logic.
   *
   * @param start - Optional start date for the Finn ad. If not provided, uses current date
   * @returns The maximum end date (start date + 41 days, or today + 41 days if no start provided)
   **/
    getFinnMaxPossible(start?: moment.Moment): moment.Moment {
      const startDate = start || moment();
      return this.promotionService.calculateMaxPossibleEndDate(
        startDate,
        this.DEFAULT_FINN_DURATION
      );
    }

    private isExisting(finn: FinnJob): boolean {
      return !!finn.id;
    }

}
